import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, MapPin, ShieldCheck, Truck } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';

const footerColumns = [
  {
    title: { mn: 'Хурдан холбоос', en: 'Quick links' },
    links: [
      { label: { mn: 'Бүтээгдэхүүн', en: 'Products' }, href: '/products' },
      { label: { mn: 'Шинэ бараа', en: 'New arrivals' }, href: '/products?filter=new' },
      { label: { mn: 'Хямдрал', en: 'Deals' }, href: '/products?sale=true' },
      { label: { mn: 'Wishlist', en: 'Wishlist' }, href: '/wishlist' },
    ],
  },
  {
    title: { mn: 'KG Goods', en: 'KG Goods' },
    links: [
      { label: { mn: 'Бидний тухай', en: 'About us' }, href: '/about' },
      { label: { mn: 'Cargo салбар', en: 'Cargo branches' }, href: '/cargo-branches' },
      { label: { mn: 'Миний захиалга', en: 'My orders' }, href: '/orders' },
      { label: { mn: 'Профайл', en: 'Profile' }, href: '/profile' },
    ],
  },
  {
    title: { mn: 'Тусламж', en: 'Support' },
    links: [
      { label: { mn: 'Нууцлал', en: 'Privacy Policy' }, href: '/privacy' },
      { label: { mn: 'Үйлчилгээний нөхцөл', en: 'Terms of Service' }, href: '/terms' },
      { label: { mn: 'Мэдээлэл устгуулах', en: 'Data Deletion' }, href: '/data-deletion' },
      { label: { mn: 'Checkout', en: 'Checkout' }, href: '/checkout' },
      { label: { mn: 'Сагс', en: 'Cart' }, href: '/cart' },
    ],
  },
];

export default function UbuyFooter() {
  const { language } = useTheme();

  return (
    <footer className="border-t border-[var(--ubuy-border-soft)] bg-white">
      <div className="mx-auto max-w-[1375px] px-[25px] py-8">
        <div className="mb-8 grid gap-4 rounded-[15px] bg-[var(--ubuy-subtle-surface)] p-4 md:grid-cols-3">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 text-[var(--ubuy-accent)]" size={22} />
            <div>
              <p className="font-semibold text-[var(--ubuy-title)]">{language === 'mn' ? 'Аюулгүй төлбөр' : 'Secure checkout'}</p>
              <p className="text-sm text-[var(--ubuy-muted)]">{language === 'mn' ? 'QPay QR болон банкны app link.' : 'QPay QR and bank app links.'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Truck className="mt-0.5 text-[var(--ubuy-accent)]" size={22} />
            <div>
              <p className="font-semibold text-[var(--ubuy-title)]">{language === 'mn' ? 'Cargo салбар' : 'Cargo branches'}</p>
              <p className="text-sm text-[var(--ubuy-muted)]">{language === 'mn' ? 'Төлбөрийн дараа ойрын салбараасаа авна.' : 'Pick up from the nearest branch after payment.'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="mt-0.5 text-[var(--ubuy-accent)]" size={22} />
            <div>
              <p className="font-semibold text-[var(--ubuy-title)]">{language === 'mn' ? 'Монгол дэмжлэг' : 'Local support'}</p>
              <p className="text-sm text-[var(--ubuy-muted)]">{language === 'mn' ? 'Монгол хэлээр захиалга өгч, лавлагаа авна.' : 'Order and get help in Mongolian, locally.'}</p>
            </div>
          </div>
        </div>

        <div className="grid gap-8 md:grid-cols-[1.2fr_1fr_1fr_1fr]">
          <div>
            <img src="/kg-goods-light-.svg" alt="KG Goods" className="h-12 w-auto" />
            <p className="mt-4 max-w-sm text-sm leading-6 text-[var(--ubuy-muted)]">
              {language === 'mn'
                ? 'Чанартай бараа онлайнаар захиалж, QPay эсвэл банкны апп-аар төлж, ойрын cargo салбараасаа авах боломжтой.'
                : 'Order quality products online, pay with QPay or your bank app, and pick up from a nearby cargo branch.'}
            </p>
            <div className="mt-4 flex gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--ubuy-border-soft)]">
                <Facebook size={17} />
              </span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--ubuy-border-soft)]">
                <Instagram size={17} />
              </span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--ubuy-border-soft)]">
                <Mail size={17} />
              </span>
            </div>
          </div>

          {footerColumns.map((column) => (
            <div key={column.title.en}>
              <p className="mb-3 text-sm font-bold text-[var(--ubuy-title)]">
                {column.title[language]}
              </p>
              <div className="space-y-2">
                {column.links.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-sm text-[var(--ubuy-muted)] hover:text-[var(--ubuy-link)]"
                  >
                    {link.label[language]}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-2 border-t border-[var(--ubuy-border-soft)] pt-4 text-xs text-[var(--ubuy-muted)] md:flex-row md:items-center md:justify-between">
          <p>© KG Goods. {language === 'mn' ? 'Онлайн захиалга, QPay төлбөр, cargo салбар.' : 'Online orders, QPay checkout, cargo branches.'}</p>
          <p>{language === 'mn' ? 'Монгол худалдан авагчдад зориулсан.' : 'Built for Mongolian shoppers.'}</p>
        </div>
      </div>
    </footer>
  );
}
